import type { Session, UserMetadata } from '@supabase/supabase-js';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { supabase, type User } from './supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signInWithGitHub: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface SupabaseUserMetadata extends UserMetadata {
  full_name?: string;
  avatar_url?: string;
}

function extractUserFromSession(session: Session): User {
  const metadata = session.user.user_metadata as SupabaseUserMetadata | undefined;
  return {
    id: session.user.id,
    email: session.user.email ?? '',
    full_name: metadata?.full_name,
    avatar_url: metadata?.avatar_url,
    email_confirmed_at: session.user.email_confirmed_at,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('[AuthProvider] Initializing auth state...');
    
    // Get initial session
    void supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('[AuthProvider] Initial session:', session ? 'exists' : 'null');
      if (session) {
        console.log('[AuthProvider] User:', session.user.email, 'verified:', session.user.email_confirmed_at);
        setSession(session);
        setUser(extractUserFromSession(session));
      }
      setLoading(false);
      console.log('[AuthProvider] Loading set to false');
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AuthProvider] Auth state change:', event, 'session:', session ? 'exists' : 'null');
      if (session) {
        console.log('[AuthProvider] User:', session.user.email, 'verified:', session.user.email_confirmed_at);
        setSession(session);
        setUser(extractUserFromSession(session));
      } else {
        console.log('[AuthProvider] Clearing user and session');
        setSession(null);
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    console.log('[AuthProvider] signIn called for:', email);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) console.error('[AuthProvider] signIn error:', error);
    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    console.log('[AuthProvider] signUp called for:', email);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/verify-email`,
      },
    });
    if (error) console.error('[AuthProvider] signUp error:', error);
    return { error };
  };

  const signOut = async () => {
    console.log('[AuthProvider] signOut called');
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const signInWithGoogle = async () => {
    console.log('[AuthProvider] signInWithGoogle called');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/app`,
      },
    });
    if (error) console.error('[AuthProvider] signInWithGoogle error:', error);
    return { error };
  };

  const signInWithGitHub = async () => {
    console.log('[AuthProvider] signInWithGitHub called');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: `${window.location.origin}/app`,
      },
    });
    if (error) console.error('[AuthProvider] signInWithGitHub error:', error);
    return { error };
  };

  const value = useMemo(() => ({
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithGitHub,
  }), [user, session, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}